'use client';

import { c as _c } from "react/compiler-runtime";
import { type Rule } from '@shared/all/domains/spaces';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import EditRules from './EditRules';
import InheritedRules from './InheritedRules';
import RuleItem from './RuleItem';
type FactiiiRulesData = {
  rules: Rule[];
  owner: {
    id: number;
    username: string;
  };
  id: number;
};
type SpaceRulesData = {
  rules: Rule[];
  spaceSlug?: string;
  membership?: {
    roles: string[];
  };
};
type Props = {
  space?: SpaceRulesData;
  factiii?: FactiiiRulesData;
  addRule: (rule: Rule) => void;
  editRule: (rule: Rule) => void;
};
const Rules = props => {
  const $ = _c(37);
  const {
    currentUser
  } = useUser();
  const {
    space,
    factiii
  } = props;
  const [modifyRulesModalOpen, setModifyRulesModalOpen] = useState(null);
  const isFactiii = Boolean(factiii);
  const rulesData = factiii || space;
  const rules = rulesData?.rules || [];
  let t0;
  if ($[0] !== currentUser || $[1] !== factiii?.owner || $[2] !== isFactiii || $[3] !== space) {
    t0 = isFactiii ? Boolean(factiii?.owner) && Boolean(currentUser.isAuthenticated) && currentUser.username === factiii?.owner?.username : space?.membership ? space.membership.roles.includes("OWNER") : false;
    $[0] = currentUser;
    $[1] = factiii?.owner;
    $[2] = isFactiii;
    $[3] = space;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const canEdit = t0;
  let t1;
  if ($[5] !== factiii || $[6] !== space) {
    t1 = factiii ? {
      factiiiId: factiii.id
    } : space?.spaceSlug ? {
      slug: space.spaceSlug
    } : undefined;
    $[5] = factiii;
    $[6] = space;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const inheritedTarget = t1;
  let t2;
  let t3;
  if ($[8] !== inheritedTarget) {
    t2 = ["spaces", "inheritedRules", inheritedTarget];
    t3 = () => unwrapFromResult(client.spaces.inheritedRules.query(inheritedTarget ?? {
      slug: ""
    }));
    $[8] = inheritedTarget;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  const t4 = Boolean(inheritedTarget);
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      select: _temp
    };
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    data: t6
  } = useQuery(t5);
  let t7;
  if ($[15] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const inheritedTiers = t7;
  const emptyMessage = isFactiii ? "This factiii has no special rules." : "This space has no special rules.";
  if (!space && !factiii) {
    let t8;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div>No data provided</div>;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    return t8;
  }
  let t8;
  if ($[18] !== canEdit) {
    t8 = canEdit && <Button text="New Rule" onClick={() => setModifyRulesModalOpen("new")} classNames="block ml-auto mt-3 mr-2" />;
    $[18] = canEdit;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = rules.length === 0;
  let t10;
  if ($[20] !== inheritedTiers || $[21] !== t9) {
    t10 = <InheritedRules tiers={inheritedTiers} expandNearest={t9} />;
    $[20] = inheritedTiers;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = rules.length === 0 && inheritedTiers.length === 0 && <p className="mb-2 ml-4 text-textSecondary/60">{emptyMessage}</p>;
  const t12 = rules.length > 0 && <div className="mt-2 space-y-2">{rules.sort(_temp2).map((rule, index) => <RuleItem key={rule.id} {...rule} id={rule.id} index={index} editClickHandler={() => setModifyRulesModalOpen(rule.id)} isOwner={canEdit} />)}</div>;
  let t13;
  if ($[23] !== canEdit || $[24] !== factiii?.id || $[25] !== modifyRulesModalOpen || $[26] !== props.addRule || $[27] !== props.editRule || $[28] !== rulesData?.rules || $[29] !== space?.spaceSlug) {
    t13 = canEdit && modifyRulesModalOpen !== null && <EditRules spaceSlug={space?.spaceSlug} factiiiId={factiii?.id} addRule={props.addRule} editRule={props.editRule} type={modifyRulesModalOpen} closeHandler={() => setModifyRulesModalOpen(null)} rule={modifyRulesModalOpen !== "new" ? rulesData?.rules?.find(x => x.id === modifyRulesModalOpen) : undefined} />;
    $[23] = canEdit;
    $[24] = factiii?.id;
    $[25] = modifyRulesModalOpen;
    $[26] = props.addRule;
    $[27] = props.editRule;
    $[28] = rulesData?.rules;
    $[29] = space?.spaceSlug;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t8) {
    t14 = <>{t8}{t10}{t11}{t12}{t13}</>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t8;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
};
export default Rules;
function _temp(data) {
  return data.tiers;
}
function _temp2(a, b) {
  return a.id - b.id;
}